import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";

import { updateWatchedFieldsUtils } from "src/app/features/Site/SiteAndSiteGroupUtils";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { InheritedFieldChangeConfirmationModal } from "../../../../../components/Modal/InheritedFieldChangeConfirmationModal";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import type { ID } from "../../../../../core/types/fe";
import { isTheSameByDeepCompare } from "../../../../../core/utils/utilFunctions";
import { useSiteGroupLoadQuery, useSiteGroupUpdateMutation } from "../../services/server/SiteGroupApi";
import type { SiteGroupEntity } from "../../types/SiteGroupEntity";

export const SiteGroupEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useSiteGroupUpdateMutation();

    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.SITE_GROUPS}`);
    const { id } = useParams();

    const [formData, setFormData] = useState<SiteGroupEntity>({} as SiteGroupEntity);
    const prevParentUnitIdRef = useRef<ID | undefined>(undefined);
    const pendingFormDataRef = useRef<SiteGroupEntity | null>(null);
    const formDataRef = useRef(formData);
    const [parentUnitId, setParentUnitId] = useState<ID | undefined>(undefined);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    // HANDLE INITIAL VALUES
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("site-group");
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useSiteGroupLoadQuery({ entityId: id });

    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
            formDataRef.current = loadFormData;
            prevParentUnitIdRef.current = loadFormData.parentUnit;
            setParentUnitId(loadFormData.parentUnit);
        }
    }, [loadFormData, setFormData]);

    const applyUpdatedData = useCallback((updatedData: SiteGroupEntity) => {
        formDataRef.current = updatedData;

        if (updatedData.parentUnit !== prevParentUnitIdRef.current) {
            setParentUnitId(updatedData.parentUnit);
            setFormData(updatedData);

            if (updatedData.parentUnit === undefined) {
                prevParentUnitIdRef.current = undefined;
            }
        }
    }, []);

    // HANDLE ON CHANGE
    const onChangeCallback = useCallback(
        (newData: SiteGroupEntity) => {
            const updatedData = {
                ...newData,
                dynamicPropertiesCheckReadOnly:
                    newData.parentUnit === undefined ? false : formDataRef.current?.dynamicPropertiesCheckReadOnly,
            };

            const hasParentUnitChange = updatedData.parentUnit !== prevParentUnitIdRef.current;
            if (hasParentUnitChange && updatedData.parentUnit !== undefined) {
                pendingFormDataRef.current = updatedData;
                setIsConfirmModalOpen(true);
                setFormData({ ...formDataRef.current });
                return;
            }

            applyUpdatedData(updatedData);
        },
        [applyUpdatedData],
    );

    const onConfirmParentUnitChange = useCallback(() => {
        const pendingData = pendingFormDataRef.current;
        pendingFormDataRef.current = null;
        setIsConfirmModalOpen(false);
        if (pendingData) {
            applyUpdatedData(pendingData);
        }
    }, [applyUpdatedData]);

    const onCancelParentUnitChange = useCallback(() => {
        pendingFormDataRef.current = null;
        setIsConfirmModalOpen(false);
        setFormData({ ...formDataRef.current });
    }, []);

    // HANDLE WATCH FIELDS
    const { data: siteGroupEntity } = useSiteGroupLoadQuery(
        { entityId: parentUnitId ? `${parentUnitId}` : undefined },
        { skip: !parentUnitId },
    );

    const updateWatchedFields = useCallback(() => {
        const currentParentUnitId = formDataRef.current?.parentUnit;

        if (!currentParentUnitId || !siteGroupEntity || String(siteGroupEntity.id) !== String(currentParentUnitId)) return null;

        prevParentUnitIdRef.current = currentParentUnitId;
        if (!formDataRef.current) return null;

        return updateWatchedFieldsUtils(siteGroupEntity, formDataRef.current);
    }, [siteGroupEntity]);

    useEffect(() => {
        if (!siteGroupEntity || !parentUnitId || String(siteGroupEntity.id) !== String(parentUnitId)) {
            return;
        }

        const prevParentUnitId = prevParentUnitIdRef.current;

        if (parentUnitId !== prevParentUnitId) {
            let newFormData = updateWatchedFields();
            if (newFormData && !isTheSameByDeepCompare(formDataRef.current, newFormData)) {
                setFormData(newFormData);
                formDataRef.current = newFormData;
                newFormData = null;
            }
        }
    }, [parentUnitId, updateWatchedFields, siteGroupEntity]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            const currentFormData = formDataRef.current;
            if (!valid || !currentFormData) {
                return;
            }

            const result = await saveFormData({ data: currentFormData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(i18n.t("common.formUpdateSuccess", { id: result.data.id, resource: "Site Group" }));
            }
        },
        [saveFormData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<SiteGroupEntity>
                    name="SiteGroupEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
            <InheritedFieldChangeConfirmationModal
                open={isConfirmModalOpen}
                onConfirm={onConfirmParentUnitChange}
                onCancel={onCancelParentUnitChange}
            />
        </Spin>
    );
};
